import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {SearchSelect, themeStore, PlotGraphMenuProps, TFilterOption, wait, exchangesStore, Button, userStore} from "@root/common"
import { PlotGraphMenuStyled } from "./PlotGraphMenu.styled"
import { observer } from "mobx-react-lite"
import { getChains, getCoins, getExchanges, } from "../../api/filters.api"
import plotmenuStore from "../../store/plotmenu.store"
import { typeOptions } from "../../const"
import { IExchange, TCoin, TType } from "../../types"


const PlotGraphMenu: FC<PlotGraphMenuProps> = observer(({onCreate, showBtn}) => {
    const plotMenuRef = useRef(null)

    const [type, setType] = useState<TFilterOption<TType>[]>([])
    const [chains, setChains] = useState<TFilterOption<string>[]>([])
    const [chainOptions, setChainOptions] = useState<TFilterOption<string>[]>([])

    const defaultCoinWasSet = useRef(false)
    const [coin, setCoin] = useState<TFilterOption<TCoin> | null>(null)
    const [coinOptions, setCoinOptions] = useState<TFilterOption<TCoin>[]>([])

    const defaultExchangeWasSet = useRef(false)
    const [exchange, setExchange] = useState<TFilterOption<IExchange> | null>(null)
    const [exchangeOptions, setExchangeOptions] = useState<TFilterOption<IExchange>[]>([])

    // Init type
    useEffect(() => {
      (async () => {
        while (plotmenuStore.defaultType === undefined) {
            await wait(100)
        }

        if (plotmenuStore.defaultType) {
            setType(plotmenuStore.defaultType.map(val => typeOptions[val]))
        }
      })()

    }, [])

    // Init chains
    useEffect(() => {
      (async () => {
        const chains = await getChains()
        setChainOptions(chains.map(chain => ({option: chain.chain, name: chain.name})))

        while (plotmenuStore.defaultChains === undefined) {
          await wait(100)
        }

        if (plotmenuStore.defaultChains == null) {
          setChains(chains.map(chain => ({option: chain.chain, name: chain.name})))
          return;
        }

        setChains(chains
          .filter(chain => plotmenuStore.defaultChains?.includes(chain.chain))
          .map(chain => ({option: chain.chain, name: chain.name}))
        )
      })()
    }, [userStore.isAuth])

    // Set coins
    useEffect(() => {
      const cb = async () => {
        if (type.length === 0 || chains.length === 0) {
          setCoinOptions([])
          return
        }

        const typesStr = type.map(val => val.option)
        const chainStr = chains.map(val => val.option)
        const coins = await getCoins(typesStr, chainStr)
        setCoinOptions(coins.map(coin => ({
          option: coin,
          name: `${coin.spread.toFixed(1)}% ${coin.coin} ...${coin.contract.slice(-3)} (${chains.find(ch => ch.option === coin.chain)?.name})`
        })))

        if (!coin) {
          const chain = chains.find(ch => ch.option === coins[0].chain)
          setCoin({
            option: coins[0],
            name: `${coins[0].spread.toFixed(1)}% ${coins[0].coin} ...${coins[0].contract.slice(-3)} (${chain?.name})`
          })
        }

        if (!defaultCoinWasSet.current) {
          defaultCoinWasSet.current = true

          while (plotmenuStore.defaultCoin === undefined) {
            await wait(100)
          }

          if (plotmenuStore.defaultCoin === null) {
            return;
          }

          const coin = coins.find(c => c?.coin?.toLowerCase() === plotmenuStore?.defaultCoin?.toLowerCase())
          if (coin) {
            const chain = chains.find(ch => ch.option === coin.chain)
            setCoin({
              option: coin,
              name: `${coin.spread.toFixed(1)}% ${coin.coin} ...${coin.contract.slice(-3)} (${chain?.name})`
            })
          }
        }
      }

      cb()

      const interval = setInterval(cb, 20e3)

      return () => {
        clearInterval(interval)
      }
    }, [coin, type, chains, userStore.isAuth])


    // Set exchange
    useEffect(() => {
      const cb = async () => {
        if (!coin) {
          setExchangeOptions([])
          return
        }

        const typesStr = type.map(val => val.option)
        const exchanges = await getExchanges(typesStr, coin.option.chain, coin.option.coin)

        while (exchangesStore.data.length === 0) {
          await wait(100)
        }

        setExchangeOptions(exchanges.map(ex => ({
          option: ex,
          name: `${exchangesStore.getNameByMarket(ex.exchange)}: ${ex.spread}% ${ex.type}`
        })))

        const firstExchange = exchanges[0]
        if (!exchange && firstExchange) {
          setExchange({
            option: firstExchange,
            name: `${exchangesStore.getNameByMarket(firstExchange.exchange)}: ${firstExchange.spread}% ${firstExchange.type}`
          })
        }

        if (!defaultExchangeWasSet.current) {
          defaultExchangeWasSet.current = true

          while (plotmenuStore.defaultExchange === undefined) {
            await wait(100)
          }

          if (plotmenuStore.defaultExchange === null) {
            return;
          }

          const exchange = exchanges.find(ex => ex.exchange.toLowerCase() === plotmenuStore.defaultExchange?.toLowerCase())
          if (exchange) {
            setExchange({
              option: exchange,
              name: `${exchangesStore.getNameByMarket(exchange.exchange)}: ${exchange.spread}% ${exchange.type}`
            })
          }
        }
      }

      cb();

      const interval = setInterval(cb, 20e3)

      return () => {
        clearInterval(interval)
      }
    }, [coin, exchangesStore.data, userStore.isAuth])

    useEffect(() => {
        window.addEventListener('keydown', e => {
            if (e.code === 'Enter') {
                //@ts-ignore
                plotMenuRef.current.children[4]?.click()
            }
        })
    }, [])

    return (
        <PlotGraphMenuStyled
            style={{backgroundColor: themeStore.bgColor, borderColor: themeStore.textColor}}
            ref={plotMenuRef}
        >
            <SearchSelect
                className="select"
                title={"Тип"}
                choice={type}
                setChoice={val => {setCoin(null); setExchange(null); setType(val)}}
                options={Object.values(typeOptions)}
            />
            <SearchSelect
                className="select"
                title={"Сеть"}
                choice={chains}
                setChoice={val => {setCoin(null); setExchange(null); setChains(val)}}
                options={chainOptions}
            />
            <SearchSelect
                className="select"
                title={"Монета"}
                choice={coin}
                isDexFut={true}
                setChoice={(val) => {setExchange(null); setCoin(val);}}
                options={coinOptions}
            />
            <SearchSelect
                className="select"
                title={"Биржа"}
                choice={exchange}
                setChoice={setExchange}
                options={exchangeOptions}
            />

            {showBtn &&
                <Button
                    onClick={() => {
                        if (exchange?.option && coin?.option) {
                            onCreate({
                                type: "futures",
                                coin: exchange.option.coin,
                                long: exchange.option.type === "long" ? exchange.option.exchange : undefined,
                                short: exchange.option.type === "short" ? exchange.option.exchange : undefined,
                                chain: coin.option.chain
                            })
                        }
                    }}
                >
                    <p>Создать</p>
                </Button>
            }
        </PlotGraphMenuStyled>
    )

})

export default PlotGraphMenu
